import axios from 'axios'
import { jwtDecode } from 'jwt-decode'
import LiveCaller from '../LiveCaller'
import { useWebSocketStore } from '../stores/websocket'
import showApiError from '../shared/toast/showApiError'
import { storage } from '@/plugins/storage'

let isRefreshing = false
const refreshSubscribers = []

// Cleanup WebSocket on session expiry
function cleanupWebSocket() {
    try {
        const websocketStore = useWebSocketStore()
        websocketStore.destroy()
    } catch (e) {
        // Ignore if store not initialized
    }
}

const instance = axios.create({})

// Don't pin Content-Type globally: axios auto-sets application/json for plain
// object bodies and multipart/form-data (with boundary) for FormData. Forcing
// application/json here made axios serialize FormData uploads to JSON, which
// turned File attachments into empty objects.
instance.defaults.headers.common.Accept = 'application/json'

instance.interceptors.request.use((config) => {
    config.baseURL = LiveCaller.config.get('api.baseURL')
    config.headers['X-LC-Locale'] = LiveCaller.config.get('app.locale', 'en')
    return new Promise((resolve) => {
        // storage is an app-independent singleton — safe at any point in startup
        const token = storage.get('oauth', {}) || {}

        if (token.token_type && token.access_token) {
            config.headers.Authorization = `${token.token_type} ${token.access_token}`
            if (token.refresh_token) {
                try {
                    const jwt = jwtDecode(token.access_token)
                    if (jwt.exp - 60 < Date.now() / 1000) {
                        if (!isRefreshing) {
                            isRefreshing = true
                            refreshAccessToken(token.refresh_token)
                                .then((newToken) => {
                                    onRefreshed(newToken)
                                })
                                .catch((e) => {
                                    onRefreshFailed(e)
                                })
                                .finally(() => {
                                    isRefreshing = false
                                })
                        }
                        return new Promise((refreshResolve, refreshReject) => {
                            subscribeTokenRefresh((newToken) => {
                                config.headers.Authorization = `${newToken.token_type} ${newToken.access_token}`
                                refreshResolve(config)
                            }, (error) => refreshReject(error))
                        }).finally(() => resolve(config))
                    }
                } catch (e) {
                    return resolve(config)
                }
            }
            return resolve(config)
        }
        return resolve(config)
    }).finally(() => Promise.resolve(config))
}, (error) => Promise.reject(error))

instance.interceptors.response.use(
    (response) => response,
    (error) => {
        const { config, response: { status } = {} } = error || {}
        const originalRequest = config

        if (error.request.responseURL.endsWith('broadcasting/batch-auth')) {
            return Promise.reject(error)
        }

        // Requests may opt out of the global toast by setting `suppressErrorToast`,
        // e.g. callers that surface their own inline error UI.
        const toastEnabled = !config?.suppressErrorToast

        // Surface server errors as a toast.
        if (status >= 500 && toastEnabled) {
            showApiError(error)
        }

        // 419 is not used with OAuth - if it occurs, treat it as unauthorized
        if (status === 419) {
            cleanupWebSocket()
            // Clear OAuth tokens and redirect to signin
            if (storage) {
                storage.remove('oauth')
                storage.remove('oauth_state')
            }
            if (toastEnabled) showApiError(error)
            try {
                LiveCaller.app.$router.push({ name: 'signin' }).catch(() => {})
            } catch (e) {
                // Ignore routing errors
            }
        }

        // Handle Session Timeouts
        if (status === 401) {
            const token = storage.get('oauth', {})
            if (token.refresh_token) {
                if (!isRefreshing) {
                    isRefreshing = true
                    refreshAccessToken(token.refresh_token)
                        .then((newToken) => {
                            onRefreshed(newToken)
                        })
                        .catch((e) => {
                            onRefreshFailed(e)
                            cleanupWebSocket()
                            if (toastEnabled) showApiError(error)
                            try {
                                LiveCaller.app.$router.push({ name: 'signin' }).catch(() => {})
                            } catch (err) {
                                // Ignore routing errors
                            }
                        })
                        .finally(() => {
                            isRefreshing = false
                        })
                }

                return new Promise((resolve, reject) => {
                    subscribeTokenRefresh((token) => {
                        originalRequest.headers.Authorization = `${token.token_type} ${token.access_token}`
                        resolve(axios(originalRequest))
                    }, () => reject(error))
                })
            }
            // No refresh token available - redirect to signin
            cleanupWebSocket()
            if (toastEnabled) showApiError(error)
            try {
                LiveCaller.app.$router.push({ name: 'signin' }).catch(() => {})
            } catch (e) {
                // Ignore routing errors
            }
        }

        // Handle Forbidden as a toast. Requests may opt out by setting
        // `suppress403` (legacy flag) or `suppressErrorToast`, e.g.
        // permission-gated widgets that degrade gracefully on their own.
        if (status === 403 && toastEnabled && !config?.suppress403) {
            showApiError(error)
        }

        return Promise.reject(error)
    },
)

function refreshAccessToken(refreshToken) {
    return new Promise((resolve, reject) => {
        // Try to use API OAuth config first, fall back to legacy
        const authConfig = LiveCaller.config.get('auth.api') || LiveCaller.config.get('auth')
        const params = {
            grant_type: 'refresh_token',
            client_id: authConfig.client_id,
            scope: authConfig.scope,
            refresh_token: refreshToken,
        }
        axios.post(`${authConfig.baseURL}/token`, params)
            .then((resp) => {
                storage.set('oauth', resp.data)
                resolve(resp.data)
            })
            .catch((e) => reject(e))
    })
}

function subscribeTokenRefresh(resolve, reject) {
    refreshSubscribers.push({
        resolve,
        reject,
    })
}

function onRefreshed(token) {
    refreshSubscribers.splice(0)
        .map((cb) => cb.resolve(token))
}

function onRefreshFailed(e) {
    refreshSubscribers.splice(0)
        .map((cb) => cb.reject(e))
}

export default instance
